import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findSkin } from '@snakefeast/shared';
import { api } from '../lib/api';
import { haptic } from '../lib/platform';
import { sfx } from '../lib/sound';
import { useSession } from '../store/session';
import { toast } from '../store/toasts';
import { SkinPreview } from './SkinPreview';
import { GameIcon, Glyph } from './GameIcon';
import { flyCoins } from '../lib/fx';

interface LoginStreakState {
  day: string;
  streak: number;
  claimedToday: boolean;
  cycleDay: number;
  cycle: Array<{ coins: number; skin?: string }>;
}

interface LoginClaim {
  state: LoginStreakState;
  reward: { coins: number; skin: string | null } | null;
}

/** Seven-day login rewards: today's tile is claimable once, a missed day starts the row over. */
export function LoginStreakCard() {
  const { t } = useTranslation();
  // undefined while loading, null if it failed (the card is then left out).
  const [state, setState] = useState<LoginStreakState | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const claimRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    void api<LoginStreakState>('/api/login-streak')
      .then((s) => !cancelled && setState(s))
      .catch(() => !cancelled && setState(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state) return null;

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api<LoginClaim>('/api/login-streak/claim', { method: 'POST', body: {} });
      setState(r.state);
      if (r.reward) {
        sfx('newBest');
        void haptic('success');
        flyCoins(claimRef.current, Math.min(12, 4 + Math.round(r.reward.coins / 8)));
        // After the coins have landed: at the top of the screen the toast would cover the counter they fly to.
        const msg = r.reward.skin ? `🎁 ${t('loginStreak.gotSkin', { coins: r.reward.coins })}` : `🪙 ${t('loginStreak.gotCoins', { coins: r.reward.coins })}`;
        window.setTimeout(() => toast(msg, 'success'), 1100);
        void useSession.getState().reloadMe().catch(() => {});
      }
    } catch {
      toast(t('errors.network'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // Days of the cycle already claimed in this streak (today included once claimed).
  const done = state.claimedToday ? state.cycleDay : state.cycleDay - 1;

  return (
    <section className={`card login-streak${state.claimedToday ? '' : ' ready'}`} aria-label={t('loginStreak.title')}>
      <div className="row">
        <h3 className="grow"><Glyph e="📅" /> {t('loginStreak.title')}</h3>
        {state.streak > 0 && <span className="pill"><Glyph e="🔥" /> {t('loginStreak.days', { count: state.streak })}</span>}
      </div>
      <ol className="login-days">
        {state.cycle.map((r, i) => {
          const n = i + 1;
          const cls = n <= done ? 'claimed' : n === state.cycleDay && !state.claimedToday ? 'today' : '';
          return (
            <li key={n} className={`${cls}${r.skin ? ' big' : ''}`}>
              <small>{t('loginStreak.day', { n })}</small>
              {r.skin ? (
                <span className="swatch small" aria-label={t('loginStreak.skinReward')}>
                  <SkinPreview skin={findSkin(r.skin)} />
                </span>
              ) : (
                <GameIcon name="coin" size={22} className="login-coin" />
              )}
              <b>{n <= done ? '✓' : r.coins}</b>
            </li>
          );
        })}
      </ol>
      {state.claimedToday ? (
        <p className="faint center" style={{ margin: '10px 0 0' }}>{t('loginStreak.comeBack')}</p>
      ) : (
        <button ref={claimRef} className="btn primary block mt" onClick={() => void claim()} disabled={busy}>
          {busy ? <span className="spin" /> : t('loginStreak.claim', { coins: state.cycle[state.cycleDay - 1]!.coins })}
        </button>
      )}
    </section>
  );
}
